import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from 'chat-interface-lib';
import { apiService } from '../services/api';
import { Loader2, ArrowLeft, ExternalLink } from 'lucide-react';
import { useTenantIcons } from '../hooks/useTenantIcons';
import * as LucideIcons from 'lucide-react';

interface EmbeddedContentData {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  embeddingUrl: string;
  title: string;
  isActive: boolean;
}

export const EmbeddedContent: React.FC = () => {
  const { embeddedId } = useParams<{ embeddedId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embeddedContent, setEmbeddedContent] = useState<EmbeddedContentData | null>(null);

  // Get tenant icons
  const tenantSlug = import.meta.env.VITE_TENANT_SLUG || 'default';
  const { getIcon } = useTenantIcons(tenantSlug);

  // Get icon components
  const Loader2Icon = getIcon('loading', 'Loader2') || Loader2;
  const ArrowLeftIcon = getIcon('arrowLeft', 'ArrowLeft') || ArrowLeft;
  const ExternalLinkIcon = getIcon('externalLink', 'ExternalLink') || ExternalLink;

  useEffect(() => {
    loadEmbeddedContent();
  }, [embeddedId]);

  const loadEmbeddedContent = async () => {
    const user = apiService.getStoredUser();
    if (!user?.tenantId || !embeddedId) {
      setError('Invalid request');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const allEmbeddedContents = await apiService.getTenantEmbeddedContents(user.tenantId);
      const content = allEmbeddedContents.find((ec: any) => ec.id === embeddedId);

      if (!content) {
        setError('Embedded content not found');
        return;
      }

      if (!content.isActive) {
        setError('This content is not available');
        return;
      }

      setEmbeddedContent(content);
    } catch (err) {
      console.error('Failed to load embedded content:', err);
      setError('Failed to load embedded content');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleOpenInNewTab = () => {
    if (embeddedContent?.embeddingUrl) {
      window.open(embeddedContent.embeddingUrl, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2Icon className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error || !embeddedContent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Content not found'}</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-muted rounded-md transition-colors"
            title="Back to home"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {embeddedContent.title || embeddedContent.name}
            </h1>
            {embeddedContent.description && (
              <p className="text-sm text-muted-foreground">{embeddedContent.description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Iframe Content */}
      <div className="flex-1 overflow-hidden">
        <iframe
          src={`${embeddedContent.embeddingUrl}${theme === 'dark' ? '#theme=night' : ''}`}
          className="w-full h-full border-0"
          title={embeddedContent.title || embeddedContent.name}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
};

